import Breadcrumb from "components/Common/Breadcrumb"
import LoadingButton from "components/Common/LoadingButton"
import { axiosbaseurl } from "configure"
import React, { useEffect, useState, useCallback, useMemo } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { Container, CardBody, Card, Row, Col } from "reactstrap"
import Select from "react-select"
import { customStyles } from "helpers/select-styles"
import { usePermissionData } from "AppContext"
import PermittedComponent from "components/Common/PermittedComponent"
import swal from "sweetalert"
import Loader from "components/Common/Loader"
import moment from "moment"
import { rangesOverlap, toMinutes } from "helpers/common_helper"
import secureLocalStorage from "react-secure-storage"

const parseJSON = raw => {
  try {
    return JSON.parse(raw || "[]")
  } catch {
    return []
  }
}

// ──────────────────────────────────────────────────────────────────────────────

const EditMeeting = () => {
  const { meetingId } = useParams()
  const navigate = useNavigate()
  const { checkAccess } = usePermissionData()
  const module_id = 30

  // ── meeting-level ──────────────────────────────────────────────────────────
  const [meeting, setMeeting] = useState({})
  const [meetingTitle, setMeetingTitle] = useState("")
  const [date, setDate] = useState("")
  const [attendees, setAttendees] = useState([])
  const [missed, setMissed] = useState([])
  const [createdAt, setCreatedAt] = useState("")

  // ── editable when no one has accepted ─────────────────────────────────────
  const [timesEditable, setTimesEditable] = useState(false)
  const [fromTime, setFromTime] = useState("")
  const [toTime, setToTime] = useState("")

  // ── per-entry state ────────────────────────────────────────────────────────
  const [entries, setEntries] = useState([])

  // ── dropdown options ───────────────────────────────────────────────────────
  const [workTypeOptions, setWorkTypeOptions] = useState([])
  const [employeeOptions, setEmployeeOptions] = useState([])
  const [clientOptions, setClientOptions] = useState([])
  const [allTaskOptions, setAllTaskOptions] = useState([])
  const [statusOptions, setStatusOptions] = useState([])
  const [serviceOptions, setServiceOptions] = useState([])

  // ── ui ─────────────────────────────────────────────────────────────────────
  const [pageLoading, setPageLoading] = useState(true)
  const [submitLoading, setSubmitLoading] = useState(false)
  const [errors, setErrors] = useState({})

  // ── 1. load static dropdown options once ──────────────────────────────────
  useEffect(() => {
    const loadOptions = async () => {
      try {
        const [wtRes, empRes, cliRes, taskRes, statusRes, svcRes] =
          await Promise.all([
            axiosbaseurl.get("/work-type"),
            axiosbaseurl.get("/employee-master"),
            axiosbaseurl.get("/client"),
            axiosbaseurl.get("/tasks/all/normal"),
            axiosbaseurl.get("/status-master"),
            axiosbaseurl.get("/service-master"),
          ])

        if (wtRes.data.success)
          setWorkTypeOptions(
            wtRes.data.data
              .filter(wt => wt.isMeeting)
              .map(wt => ({ value: wt.id, label: wt.workType }))
          )

        if (empRes.data.success)
          setEmployeeOptions(
            empRes.data.data.map(e => ({
              value: e.id,
              label: `${e.name} ${e.lastName || ""}`.trim(),
            }))
          )

        if (cliRes.data.success)
          setClientOptions(
            cliRes.data.data.map(c => ({
              value: c.id,
              label: `${c.clientCode || ""} - ${c.clientName}`,
              clientTypeId: c.clientTypeId,
              services: c.serviceNameId
                ? JSON.parse(JSON.parse(c.serviceNameId))
                : [],
            }))
          )

        if (taskRes.data.success)
          setAllTaskOptions(
            taskRes.data.data.map(t => ({
              value: t.id,
              label: `${t.title} - ${moment(t.startDate).format("DD/MM/YYYY")}`,
              clientId: t.clientId,
              serviceId: t.serviceNameId,
            }))
          )
        if (statusRes.data.success)
          setStatusOptions(
            statusRes.data.data
              .slice()
              .sort((a, b) => a.statusName.localeCompare(b.statusName))
              .map(t => ({ value: t.id, label: t.statusName }))
          )

        if (svcRes.data.success)
          setServiceOptions(
            svcRes.data.data
              .slice()
              .sort((a, b) => a.serviceName.localeCompare(b.serviceName))
              .map(s => ({
                value: s.id,
                label: s.serviceName,
                clientTypeId: s.clientTypeId,
                category: s.serviceCategory,
              }))
          )
      } catch (err) {
        console.error("Options load failed:", err)
      }
    }

    loadOptions()
  }, [])

  // ── 2. load meeting once options are ready ─────────────────────────────────
  useEffect(() => {
    if (!employeeOptions.length || !clientOptions.length) return

    const fetchMeeting = async () => {
      try {
        const res = await axiosbaseurl.get(`/meeting/${meetingId}`)
        if (!res.data.success) throw new Error("API returned failure")

        const m = res.data.data

        // ── meeting-level ────────────────────────────────────────────────────
        setMeetingTitle(m.meetingTitle || m.title || "")
        setDate(m.date ? m.date.split("T")[0] : "")
        setFromTime(m.fromTime || "")
        setToTime(m.toTime || "")
        setCreatedAt(m.createdAt || "")
        setMeeting(m)

        // ── timesEditable = true only when accepted array is empty ────────────
        const acceptedArr = parseJSON(m.accepted)
        setTimesEditable(acceptedArr.length === 0)

        // ── attendees / missed (display only) ────────────────────────────────
        const attendeeIds = parseJSON(
          typeof m.attendees === "string"
            ? m.attendees
            : JSON.stringify(m.attendees || [])
        )
        const missedIds = parseJSON(
          typeof m.missed === "string"
            ? m.missed
            : JSON.stringify(m.missed || [])
        )
        setAttendees(employeeOptions.filter(e => attendeeIds.includes(e.value)))
        setMissed(employeeOptions.filter(e => missedIds.includes(e.value)))

        // ── per-entry rows ────────────────────────────────────────────────────
        const rawEntries =
          typeof m.entries === "string"
            ? JSON.parse(m.entries)
            : m.entries || []

        setEntries(
          rawEntries.map((entry, idx) => ({
            id: entry.id ?? idx + 1,
            client: clientOptions.find(c => c.value === entry.clientId) || null,
            service:
              serviceOptions.find(s => s.value === entry.serviceId) || null,
            task: entry.taskId
              ? allTaskOptions.find(t => t.value === entry.taskId) ||
                (entry.task
                  ? { value: entry.task.id, label: entry.task.title }
                  : { value: entry.taskId, label: `Task #${entry.taskId}` })
              : null,
            status: entry.statusId
              ? statusOptions.find(s => s.value === entry.statusId) || null
              : null,
            workType:
              workTypeOptions.find(wt => wt.value === entry.workTypeId) || null,
            inTime: entry.inTime || "",
            outTime: entry.outTime || "",
            agenda: entry.agenda || "",
          }))
        )
      } catch (err) {
        console.error(err)
        swal("Error", "Failed to load meeting data", "error")
        navigate(-1)
      } finally {
        setPageLoading(false)
      }
    }

    fetchMeeting()
  }, [
    workTypeOptions,
    employeeOptions,
    clientOptions,
    allTaskOptions,
    statusOptions,
    serviceOptions,
  ])

  // ── entry updater ──────────────────────────────────────────────────────────
  const updateEntry = useCallback((id, field, value) => {
    setEntries(prev =>
      prev.map(entry =>
        entry.id === id ? { ...entry, [field]: value } : entry
      )
    )
  }, [])

  const getTaskOptionsForClient = useCallback(
    (clientValue, serviceValue) => {
      if (!clientValue) return []
      return allTaskOptions.filter(t => {
        const matchesClient = t.clientId === clientValue
        const matchesService = serviceValue
          ? t.serviceId === serviceValue
          : true
        return matchesClient && matchesService
      })
    },
    [allTaskOptions]
  )

  // ── helper: format minutes as "Xh Ym" ────────────────────────────────────
  const formatMinutes = mins => {
    const h = Math.floor(mins / 60)
    const m = mins % 60
    if (h > 0 && m > 0) return `${h}h ${m}m`
    if (h > 0) return `${h}h`
    return `${m}m`
  }

  // ── validation ─────────────────────────────────────────────────────────────
  const validate = () => {
    const tempErrors = {}

    if (timesEditable) {
      // ── meeting-level time range ───────────────────────────────────────────
      if (!fromTime && meeting.fromTime) {
        tempErrors.fromTime = "From time is required"
      }
      if (!toTime && meeting.toTime) {
        tempErrors.toTime = "To time is required"
      }
      if (fromTime && toTime && toMinutes(fromTime) >= toMinutes(toTime)) {
        tempErrors.toTime = "To time must be after from time"
      }

      // ── per-entry times must stay within fromTime–toTime ──────────────────
      const meetingStart = toMinutes(fromTime)
      const meetingEnd = toMinutes(toTime)
      const hasMeetingRange = meetingStart !== null && meetingEnd !== null

      entries.forEach(entry => {
        if (!entry.inTime) {
          tempErrors[`entry_${entry.id}_inTime`] = "In time is required"
        } else if (hasMeetingRange) {
          const entryIn = toMinutes(entry.inTime)
          if (entryIn < meetingStart)
            tempErrors[
              `entry_${entry.id}_inTime`
            ] = `Cannot be before meeting start (${fromTime})`
          else if (entryIn >= meetingEnd)
            tempErrors[
              `entry_${entry.id}_inTime`
            ] = `Cannot be at or after meeting end (${toTime})`
        }

        if (!entry.outTime) {
          tempErrors[`entry_${entry.id}_outTime`] = "Out time is required"
        } else if (hasMeetingRange) {
          const entryOut = toMinutes(entry.outTime)
          if (entryOut > meetingEnd)
            tempErrors[
              `entry_${entry.id}_outTime`
            ] = `Cannot exceed meeting end (${toTime})`
          else if (entryOut <= meetingStart)
            tempErrors[
              `entry_${entry.id}_outTime`
            ] = `Cannot be at or before meeting start (${fromTime})`
        }

        if (
          entry.inTime &&
          entry.outTime &&
          !tempErrors[`entry_${entry.id}_inTime`] &&
          !tempErrors[`entry_${entry.id}_outTime`] &&
          toMinutes(entry.inTime) >= toMinutes(entry.outTime)
        ) {
          tempErrors[`entry_${entry.id}_outTime`] =
            "Out time must be after in time"
        }
      })

      for (let i = 0; i < entries.length; i++) {
        for (let j = i + 1; j < entries.length; j++) {
          const a = entries[i],
            b = entries[j]
          if (
            rangesOverlap(a.inTime, a.outTime, b.inTime, b.outTime) &&
            !tempErrors[`entry_${a.id}_inTime`] &&
            !tempErrors[`entry_${a.id}_outTime`] &&
            !tempErrors[`entry_${b.id}_inTime`] &&
            !tempErrors[`entry_${b.id}_outTime`]
          ) {
            tempErrors[`entry_${a.id}_outTime`] = `Entry ${
              i + 1
            } overlaps with Entry ${j + 1} (${b.inTime}–${b.outTime})`
            tempErrors[`entry_${b.id}_inTime`] = `Entry ${
              j + 1
            } overlaps with Entry ${i + 1} (${a.inTime}–${a.outTime})`
          }
        }
      }

      // ── Commit field-level errors before duration check ───────────────────
      setErrors(tempErrors)
      if (Object.keys(tempErrors).length > 0) return false

      // ── Total duration check (only when times are editable & both set) ─────
      if (hasMeetingRange) {
        const totalMeetingMins = meetingEnd - meetingStart

        const totalEntryMins = entries.reduce((sum, entry) => {
          const inMin = toMinutes(entry.inTime)
          const outMin = toMinutes(entry.outTime)
          if (inMin !== null && outMin !== null && outMin > inMin) {
            return sum + (outMin - inMin)
          }
          return sum
        }, 0)

        if (totalEntryMins !== totalMeetingMins) {
          const meetingDuration = formatMinutes(totalMeetingMins)
          const entriesDuration = formatMinutes(totalEntryMins)
          const diff = Math.abs(totalMeetingMins - totalEntryMins)
          const isShort = totalEntryMins < totalMeetingMins

          swal({
            title: "Duration Mismatch",
            text: `Meeting duration is ${meetingDuration}, but your entries total ${entriesDuration}. You are ${
              isShort ? "short by" : "over by"
            } ${formatMinutes(
              diff
            )}. Please adjust your entry times so they add up to ${meetingDuration}.`,
            icon: "warning",
            button: "OK",
          })
          return false
        }
      }
    } else {
      // ── Times are locked — only agenda edits allowed, no time validation ───
      setErrors(tempErrors)
    }

    return true
  }

  // ── submit ─────────────────────────────────────────────────────────────────
  const handleSubmit = async e => {
    e.preventDefault()
    if (!validate()) return

    const updatedEntries = entries.map(entry => ({
      id: entry.id,
      agenda: entry.agenda.trim() || null,
      ...(timesEditable && {
        inTime: entry.inTime,
        outTime: entry.outTime,
      }),
    }))

    const payload = {
      entries: JSON.stringify(updatedEntries),
      // include fromTime/toTime only when editable
      ...(timesEditable && { fromTime, toTime }),
    }

    try {
      setSubmitLoading(true)
      const res = await axiosbaseurl.put(`/meeting/${meetingId}`, payload)
      if (res.data.success) {
        swal("Success", "Meeting updated successfully", "success")
        navigate(-1)
      }
    } catch (err) {
      console.error(err)
      swal(
        "Error",
        err.response?.data?.message || "Failed to update meeting",
        "error"
      )
    } finally {
      setSubmitLoading(false)
    }
  }

  const isToday = createdAt
    ? moment(createdAt).format("YYYY-MM-DD") === moment().format("YYYY-MM-DD")
    : false

  const loggedInUser = secureLocalStorage.getItem("authUser")

  const isAttendee = attendees.find(
    emp => emp.value == loggedInUser?.employeeId
  )

  document.title = "Edit Meeting | Peacock Accountants Limited"

  if (pageLoading) return <Loader />

  return (
    <div className="page-content mb-5">
      <Container fluid>
        <Breadcrumb title="Dashboard" breadcrumbItem="Edit Meeting" />

        <Card>
          <CardBody>
            <PermittedComponent isAccess={checkAccess(module_id, "edit")}>
              <form onSubmit={handleSubmit}>
                <div className="text-end mb-4">
                  {isToday && isAttendee && (
                    <LoadingButton
                      loading={submitLoading}
                      btnText="Update Meeting"
                    />
                  )}
                  <button
                    type="button"
                    className="btn btn-secondary bg-dark ms-3"
                    onClick={() => navigate(-1)}
                  >
                    <i className="fas fa-arrow-left me-2" />
                    Back
                  </button>
                </div>

                <hr className="mb-2" />

                {/* ── Meeting-level header ───────────────────────────────────── */}
                <Row className="mb-3 g-4">
                  {/* Title — always read-only */}
                  <Col md={8}>
                    <label className="form-label">Meeting Title</label>
                    <input
                      type="text"
                      className="form-control bg-light"
                      value={meetingTitle}
                      disabled
                    />
                  </Col>

                  {/* From Time — editable only when timesEditable */}
                  <Col md={2}>
                    <label className="form-label">
                      From Time
                      {timesEditable && <span className="text-danger"> *</span>}
                    </label>
                    <input
                      type="time"
                      className="form-control"
                      value={fromTime}
                      disabled={!timesEditable}
                      onChange={
                        timesEditable
                          ? e => setFromTime(e.target.value)
                          : undefined
                      }
                    />
                    {errors.fromTime && (
                      <small className="text-danger d-block">
                        {errors.fromTime}
                      </small>
                    )}
                  </Col>

                  {/* To Time — editable only when timesEditable */}
                  <Col md={2}>
                    <label className="form-label">
                      To Time
                      {timesEditable && <span className="text-danger"> *</span>}
                    </label>
                    <input
                      type="time"
                      className="form-control"
                      value={toTime}
                      disabled={!timesEditable}
                      onChange={
                        timesEditable
                          ? e => setToTime(e.target.value)
                          : undefined
                      }
                    />
                    {errors.toTime && (
                      <small className="text-danger d-block">
                        {errors.toTime}
                      </small>
                    )}
                  </Col>

                  {/* Attendees — always display-only */}
                  <Col md={6}>
                    <label className="form-label">Attendees (Present)</label>
                    <Select
                      isMulti
                      value={attendees}
                      options={attendees}
                      styles={customStyles}
                      isDisabled
                      placeholder="—"
                    />
                  </Col>

                  {/* Missed — always display-only */}
                  <Col md={6}>
                    <label className="form-label">
                      Missed / Absent / Declined
                    </label>
                    <Select
                      isMulti
                      value={missed}
                      options={missed}
                      styles={customStyles}
                      isDisabled
                      placeholder="—"
                    />
                  </Col>
                </Row>

                {/* ── Lock notice ────────────────────────────────────────────── */}
                {!timesEditable && (
                  <div className="alert alert-warning d-flex align-items-center gap-2 mb-4 py-2">
                    <i className="fas fa-lock me-1" />
                    <span>
                      Times are <strong>locked</strong> — one or more attendees
                      have already accepted this meeting.{" "}
                      <strong>Only agendas</strong> can be edited.
                    </span>
                  </div>
                )}

                {/* ── Per-entry rows ─────────────────────────────────────────── */}
                {entries.map(entry => (
                  <EditEntryRow
                    key={entry.id}
                    entry={entry}
                    date={date}
                    fromTime={fromTime}
                    toTime={toTime}
                    timesEditable={timesEditable}
                    errors={errors}
                    updateEntry={updateEntry}
                    serviceOptions={serviceOptions}
                    statusOptions={statusOptions}
                    clientOptions={clientOptions}
                    taskOptions={getTaskOptionsForClient(
                      entry.client?.value,
                      entry.service?.value
                    )}
                  />
                ))}
              </form>
            </PermittedComponent>
          </CardBody>
        </Card>
      </Container>
    </div>
  )
}

// ─── EditEntryRow ──────────────────────────────────────────────────────────────
const EditEntryRow = React.memo(
  ({
    entry,
    fromTime,
    toTime,
    timesEditable,
    errors,
    updateEntry,
    serviceOptions,
    statusOptions,
    taskOptions,
  }) => {
    const filteredServices = useMemo(() => {
      if (!entry.client) return serviceOptions

      const clientOwnedServiceIds = entry.client.services?.map(s => s.id) ?? []

      return serviceOptions.filter(
        svc =>
          clientOwnedServiceIds.includes(svc.value) ||
          ((svc.category === "Non-Billiable and Non-scheduled" ||
            svc.category === "Non-Billiable and Scheduled") &&
            (svc.clientTypeId === entry.client.clientTypeId ||
              svc.clientTypeId === 0))
      )
    }, [entry.client, serviceOptions])

    const handleInTimeChange = useCallback(
      e => updateEntry(entry.id, "inTime", e.target.value),
      [entry.id, updateEntry]
    )
    const handleOutTimeChange = useCallback(
      e => updateEntry(entry.id, "outTime", e.target.value),
      [entry.id, updateEntry]
    )
    const handleAgendaChange = useCallback(
      e => updateEntry(entry.id, "agenda", e.target.value),
      [entry.id, updateEntry]
    )

    const rangeHint =
      timesEditable && fromTime && toTime ? (
        <small className="text-muted">{`Allowed: ${fromTime} – ${toTime}`}</small>
      ) : null

    return (
      <div className="border p-4 mb-4 bg-light rounded">
        <Row className="g-3">
          {/* Client — read-only */}
          <Col md={4}>
            <label className="form-label">Client</label>
            <Select
              value={entry.client}
              options={entry.client ? [entry.client] : []}
              styles={customStyles}
              isDisabled
              placeholder="—"
            />
          </Col>

          {/* Service — read-only */}
          <Col md={4}>
            <label className="form-label">Service</label>
            <Select
              value={entry.service}
              options={filteredServices}
              styles={customStyles}
              isDisabled
              placeholder="—"
            />
          </Col>

          {/* Task — read-only */}
          <Col md={4}>
            <label className="form-label">Related Task</label>
            <Select
              value={entry.task}
              options={taskOptions}
              styles={customStyles}
              isDisabled
              placeholder="—"
            />
          </Col>

          {/* Task Status — read-only */}
          <Col md={4}>
            <label className="form-label">Task Status</label>
            <Select
              value={entry.status}
              options={statusOptions}
              styles={customStyles}
              isDisabled
              placeholder="—"
            />
          </Col>

          {/* Work Type — read-only */}
          <Col md={4}>
            <label className="form-label">Work Type</label>
            <Select
              value={entry.workType}
              options={entry.workType ? [entry.workType] : []}
              styles={customStyles}
              isDisabled
              placeholder="—"
            />
          </Col>

          {/* In Time */}
          <Col md={2}>
            <label className="form-label">
              In Time {timesEditable && <span className="text-danger">*</span>}
            </label>
            <input
              type="time"
              className="form-control"
              value={entry.inTime}
              disabled={!timesEditable}
              min={timesEditable ? fromTime || undefined : undefined}
              max={timesEditable ? toTime || undefined : undefined}
              onChange={timesEditable ? handleInTimeChange : undefined}
            />
            {rangeHint}
            {errors[`entry_${entry.id}_inTime`] && (
              <small className="text-danger d-block">
                {errors[`entry_${entry.id}_inTime`]}
              </small>
            )}
          </Col>

          {/* Out Time */}
          <Col md={2}>
            <label className="form-label">
              Out Time {timesEditable && <span className="text-danger">*</span>}
            </label>
            <input
              type="time"
              className="form-control"
              value={entry.outTime}
              disabled={!timesEditable}
              min={timesEditable ? fromTime || undefined : undefined}
              max={timesEditable ? toTime || undefined : undefined}
              onChange={timesEditable ? handleOutTimeChange : undefined}
            />
            {rangeHint}
            {errors[`entry_${entry.id}_outTime`] && (
              <small className="text-danger d-block">
                {errors[`entry_${entry.id}_outTime`]}
              </small>
            )}
          </Col>

          {/* Agenda — always editable */}
          <Col md={12}>
            <label className="form-label">
              Agenda / Discussion Points / Remarks
            </label>
            <textarea
              className="form-control"
              rows={5}
              value={entry.agenda}
              onChange={handleAgendaChange}
              placeholder="What was discussed / decided for this client..."
            />
          </Col>
        </Row>
      </div>
    )
  }
)

EditEntryRow.displayName = "EditEntryRow"

export default EditMeeting
